import React, { useState } from 'react'
import { User, HourlyRateFormData } from '../../../types'
import { X, DollarSign } from 'lucide-react'

interface HourlyRateModalProps {
  user: User
  onSubmit: (data: HourlyRateFormData) => void
  onCancel: () => void
  isLoading: boolean
}

const HourlyRateModal: React.FC<HourlyRateModalProps> = ({
  user,
  onSubmit,
  onCancel,
  isLoading
}) => {
  const [formData, setFormData] = useState<HourlyRateFormData>({
    newRate: user.hourlyRate || 0,
    reason: ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleInputChange = (field: keyof HourlyRateFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // 清除該欄位的錯誤
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.newRate && formData.newRate !== 0) {
      newErrors.newRate = '新時薪為必填欄位'
    } else if (!Number.isInteger(formData.newRate) || formData.newRate < 0) {
      newErrors.newRate = '時薪必須為正整數'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    onSubmit(formData)
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-md w-full">
        {/* 標題 */}
        <div className="flex items-center justify-between p-6 border-b border-primary-200">
          <div className="flex items-center space-x-3">
            <DollarSign className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-secondary-800">設定基本時薪</h2>
          </div>
          <button
            onClick={onCancel}
            className="text-secondary-400 hover:text-secondary-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 表單內容 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 用戶資訊 */}
          <div className="bg-primary-50 rounded-lg p-4">
            <p className="text-sm text-secondary-700">
              <span className="font-medium">成員:</span> {user.name} ({user.email})
            </p>
            {user.hourlyRate && (
              <p className="text-sm text-secondary-600 mt-1">
                <span className="font-medium">目前時薪:</span> {user.currency} {user.hourlyRate}/小時
              </p>
            )}
          </div>

          {/* 新時薪 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              新的基本時薪 (整數) <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center space-x-2">
              <span className="text-secondary-600">NT$</span>
              <input
                type="number"
                min="0"
                step="1"
                value={formData.newRate}
                onChange={(e) => handleInputChange('newRate', parseInt(e.target.value) || 0)}
                className={`input-primary flex-1 ${errors.newRate ? 'input-error' : ''}`}
                placeholder="600"
              />
              <span className="text-secondary-600">/小時</span>
            </div>
            {errors.newRate && (
              <p className="text-red-500 text-sm mt-1">{errors.newRate}</p>
            )}
          </div>

          {/* 變更原因 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              變更原因 (選填)
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              className="input-primary resize-none"
              rows={3}
              placeholder="年度調薪..."
            />
          </div>

          {/* 警告訊息 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 text-yellow-600 mt-0.5">⚠️</div>
              <p className="text-sm text-yellow-800">
                此變更將立即生效，影響未來的工時計算
              </p>
            </div>
          </div>

          {/* 操作按鈕 */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-primary-200">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={isLoading}
            >
              取消
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="loading-spinner w-4 h-4"></div>
                  <span>更新中...</span>
                </div>
              ) : (
                '確認變更'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default HourlyRateModal
